/**
 * Group Member Manager
 * Slide-over panel for managing distribution group members
 */
import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  UserGroupIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
  EnvelopeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  useGroupMembers,
  useAddGroupMember,
  useRemoveGroupMember,
  useUpdateGroupMemberRole,
  useLocalDirectorySearch,
} from '../../hooks/useDistributionGroups';
import type { DistributionGroup, GroupMember, MemberType, MemberRole } from '../../types/admin.types';

interface GroupMemberManagerProps {
  group: DistributionGroup | null;
  isOpen: boolean;
  onClose: () => void;
}

export const GroupMemberManager: React.FC<GroupMemberManagerProps> = ({
  group,
  isOpen,
  onClose,
}) => {
  // State for member list
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // State for adding members
  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [selectedMemberType, setSelectedMemberType] = useState<MemberType>('mailbox');
  const [externalEmail, setExternalEmail] = useState('');
  const [externalName, setExternalName] = useState('');

  // State for delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<GroupMember | null>(null);

  // Fetch members
  const { data: membersData, isLoading } = useGroupMembers(group?.group_id || null, {
    search: search || undefined,
    page,
    pageSize,
  });

  // Fetch tenant-local mailboxes for adding internal members.
  const { data: localDirectoryData } = useLocalDirectorySearch({
    search: addMemberSearch || undefined,
    scope: 'colleagues',
    limit: 10,
  });

  // Mutations
  const addMemberMutation = useAddGroupMember();
  const removeMemberMutation = useRemoveGroupMember();
  const updateRoleMutation = useUpdateGroupMemberRole();

  // Reset state when panel opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setPage(1);
      setShowAddMember(false);
      setAddMemberSearch('');
      setExternalEmail('');
      setExternalName('');
    }
  }, [isOpen]);

  // Handlers
  const handleAddInternalMember = async (email: string, displayName: string, mailboxId?: string) => {
    if (!group) return;

    try {
      await addMemberMutation.mutateAsync({
        groupId: group.group_id,
        data: {
          memberId: mailboxId,
          memberEmail: email,
          memberDisplayName: displayName,
          memberType: 'mailbox',
          memberRole: 'member',
          isExternal: false,
        },
      });
      setShowAddMember(false);
      setAddMemberSearch('');
    } catch (err) {
      console.error('Failed to add member:', err);
    }
  };

  const handleAddExternalMember = async () => {
    if (!group || !externalEmail) return;

    try {
      await addMemberMutation.mutateAsync({
        groupId: group.group_id,
        data: {
          memberEmail: externalEmail,
          memberDisplayName: externalName || externalEmail,
          memberType: 'contact',
          memberRole: 'member',
          isExternal: true,
        },
      });
      setShowAddMember(false);
      setExternalEmail('');
      setExternalName('');
    } catch (err) {
      console.error('Failed to add external member:', err);
    }
  };

  const handleRemoveMember = async (member: GroupMember) => {
    if (!group) return;

    try {
      await removeMemberMutation.mutateAsync({
        groupId: group.group_id,
        membershipId: member.membership_id,
      });
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to remove member:', err);
    }
  };

  const handleUpdateRole = async (member: GroupMember, newRole: MemberRole) => {
    if (!group) return;

    try {
      await updateRoleMutation.mutateAsync({
        groupId: group.group_id,
        membershipId: member.membership_id,
        memberRole: newRole,
      });
    } catch (err) {
      console.error('Failed to update role:', err);
    }
  };

  // Get member type icon
  const getMemberTypeIcon = (type: MemberType) => {
    switch (type) {
      case 'mailbox':
        return <UserIcon className="w-4 h-4" />;
      case 'group':
        return <UsersIcon className="w-4 h-4" />;
      case 'contact':
        return <EnvelopeIcon className="w-4 h-4" />;
    }
  };

  // Get role badge
  const getRoleBadge = (role: MemberRole) => {
    const badges: Record<MemberRole, { label: string; className: string }> = {
      member: {
        label: 'Member',
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      },
      owner: {
        label: 'Owner',
        className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      },
      moderator: {
        label: 'Moderator',
        className: 'bg-lex-accent-soft text-lex-accent',
      },
    };
    const badge = badges[role];
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  if (!isOpen || !group) return null;

  const members = membersData?.members || [];
  const totalPages = membersData?.totalPages || 1;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Panel */}
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl flex">
        <div className="w-full bg-white dark:bg-gray-800 shadow-xl flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-lex-accent-soft rounded-lg">
                  <UserGroupIcon className="w-6 h-6 text-lex-accent" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {group.display_name}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{group.email}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Toolbar */}
          <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search members..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent"
              />
            </div>
            <button
              onClick={() => setShowAddMember(true)}
              className="inline-flex items-center px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Member
            </button>
          </div>

          {/* Member List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
              </div>
            ) : members.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                <UsersIcon className="w-12 h-12 mb-4" />
                <p className="text-lg font-medium">No members yet</p>
                <p className="text-sm">Add members to this distribution group</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {members.map((member) => (
                  <div
                    key={member.membership_id}
                    className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className={`p-2 rounded-lg ${
                          member.is_external
                            ? 'bg-amber-100 dark:bg-amber-900/30'
                            : 'bg-gray-100 dark:bg-gray-700'
                        }`}
                      >
                        {getMemberTypeIcon(member.member_type)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {member.member_display_name || member.member_email}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {member.member_email}
                          {member.is_external && (
                            <span className="ml-2 text-amber-600 dark:text-amber-400">
                              (External)
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <select
                        value={member.member_role}
                        onChange={(e) => handleUpdateRole(member, e.target.value as MemberRole)}
                        className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="member">Member</option>
                        <option value="owner">Owner</option>
                        <option value="moderator">Moderator</option>
                      </select>
                      <button
                        onClick={() => setDeleteConfirm(member)}
                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {members.length > 0 && (
            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {membersData?.total || 0} member(s)
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="p-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="p-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add Member</h3>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Member Type Tabs */}
              <div className="flex space-x-2">
                <button
                  onClick={() => setSelectedMemberType('mailbox')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg ${
                    selectedMemberType === 'mailbox'
                      ? 'bg-lex-accent-soft text-lex-accent'
                      : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  Internal Mailbox
                </button>
                <button
                  disabled
                  title="External contacts are not enabled for this WeBeMail distribution-group slice."
                  className="flex-1 px-3 py-2 text-sm font-medium rounded-lg text-gray-400 bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
                >
                  External Contact
                </button>
              </div>

              {selectedMemberType === 'mailbox' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Search Mailboxes
                  </label>
                  <input
                    type="text"
                    value={addMemberSearch}
                    onChange={(e) => setAddMemberSearch(e.target.value)}
                    placeholder="Search by name or email..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />

                  {/* Mailbox List */}
                  <div className="mt-3 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    {localDirectoryData?.contacts.length === 0 ? (
                      <p className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                        No mailboxes found
                      </p>
                    ) : (
                      localDirectoryData?.contacts.map((mailbox) => (
                        <button
                          key={mailbox.id}
                          onClick={() =>
                            handleAddInternalMember(
                              mailbox.email,
                              mailbox.displayName || mailbox.email,
                              String(mailbox.metadata?.mailboxId || mailbox.id)
                            )
                          }
                          disabled={addMemberMutation.isPending}
                          className="w-full px-4 py-3 flex items-center hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                        >
                          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg mr-3">
                            <UserIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {mailbox.displayName || mailbox.email}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {mailbox.email}
                            </p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={externalEmail}
                      onChange={(e) => setExternalEmail(e.target.value)}
                      placeholder="external@example.com"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={externalName}
                      onChange={(e) => setExternalName(e.target.value)}
                      placeholder="John Doe"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <button
                    onClick={handleAddExternalMember}
                    disabled={!externalEmail || addMemberMutation.isPending}
                    className="w-full px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:opacity-50"
                  >
                    {addMemberMutation.isPending ? 'Adding...' : 'Add External Contact'}
                  </button>
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowAddMember(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Remove Member
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to remove{' '}
              <strong>{deleteConfirm.member_display_name || deleteConfirm.member_email}</strong>{' '}
              from this group?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRemoveMember(deleteConfirm)}
                disabled={removeMemberMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {removeMemberMutation.isPending ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupMemberManager;
